var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../../../../atoms/Button/Button';
/**
 * Detect supported audio MIME type for MediaRecorder
 * Checks common types with Safari fallback support
 */
const getSupportedMimeType = () => {
    const types = [
        { mimeType: 'audio/webm', extension: 'webm' },
        { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
        { mimeType: 'audio/mp4', extension: 'mp4' },
        { mimeType: 'audio/mpeg', extension: 'mp3' }
    ];
    const supportedType = types.find((type) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type.mimeType));
    // Return supported type or fallback to webm
    return supportedType || { mimeType: 'audio/webm', extension: 'webm' };
};
/**
 * AudioRecorder Component
 * Handles audio recording functionality with browser-specific MIME type detection
 * @version 1.0.0
 * @since 2026-02-17
 */
export function AudioRecorder({ onRecordingComplete, className = '', baseClass }) {
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const mimeTypeRef = useRef(getSupportedMimeType());
    const handleRecordAudio = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (isRecording) {
            // Stop recording
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
                // mediaRecorderRef will be cleared in onstop handler
            }
            return;
        }
        // Start recording
        try {
            const stream = yield navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const supportedType = getSupportedMimeType();
            mimeTypeRef.current = supportedType;
            const mediaRecorder = new MediaRecorder(stream, { mimeType: supportedType.mimeType });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];
            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };
            mediaRecorder.onstop = () => {
                try {
                    const { mimeType, extension } = mimeTypeRef.current;
                    const blob = new Blob(chunksRef.current, { type: mimeType });
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const file = new File([blob], `recording-${timestamp}.${extension}`, { type: mimeType });
                    onRecordingComplete(file);
                }
                finally {
                    // Always stop tracks and cleanup, even if onRecordingComplete throws
                    setIsRecording(false);
                    if (streamRef.current) {
                        streamRef.current.getTracks().forEach((track) => track.stop());
                        streamRef.current = null;
                    }
                    mediaRecorderRef.current = null;
                }
            };
            mediaRecorder.start();
            setIsRecording(true);
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error accessing microphone:', error);
            setIsRecording(false);
            // Clean up stream if it was created but initialization failed
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
            mediaRecorderRef.current = null;
        }
    }), [isRecording, onRecordingComplete]);
    // Cleanup effect to stop recording and release media tracks on unmount
    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                // Remove event handlers to prevent state updates after unmount
                mediaRecorderRef.current.ondataavailable = null;
                mediaRecorderRef.current.onstop = null;
                mediaRecorderRef.current.stop();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
        };
    }, []);
    const recordingClass = baseClass && isRecording ? `${baseClass}__recording` : '';
    const buttonClasses = [className, recordingClass].filter(Boolean).join(' ');
    return (React.createElement(Button, { "aria-label": isRecording ? 'Stop recording' : 'Record audio', onClick: () => {
            handleRecordAudio().catch((err) => console.error('Recording error:', err));
        }, className: buttonClasses },
        React.createElement("i", { className: isRecording ? 'fa-light fa-stop' : 'fa-light fa-microphone' })));
}
